/// <amd-module name="Wit/Deployment/Components/Deployment" />
import * as React from "react";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ExternalDeployment } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { PipelineCard } from "Wit/Deployment/Components/PipelineCard";
import { DeploymentIntegrationConstants, environmentTypeOrderMap } from "Wit/Deployment/Constants";
import { IDeploymentType, IStage } from "Wit/Deployment/Contracts";
import * as Resources from "Wit/Deployment/Resources";
import {
    DeploymentsIntegrationCIActions,
    publishDeploymentsIntegrationTrace,
    publishDeploymentsTelemetry
} from "Wit/Deployment/telemetry/Deployments";
import { IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";

export interface IDeploymentProps {
    workItemId: number;
    projectName: string;
    onRegisterWorkItemListener?: (workItemNotificationListener: IWorkItemNotificationListener) => void;
}

export interface IDeploymentState {
    deployments: IDeploymentType[] | null;
    workItemId: number;
    isLoading: boolean;
    hasError: boolean;
}

export class Deployment extends VssComponent<IDeploymentProps, IDeploymentState> {
    public static getDerivedStateFromProps(props: IDeploymentProps, state: IDeploymentState) {
        if (props.workItemId !== state.workItemId) {
            // When props updated, the component display "old" data and loading the new data with a delay.
            // This is to prevent constantly showing zero data component which causes flakiness when fast switching work items.
            return {
                deployments: state.deployments,
                workItemId: props.workItemId,
                isLoading: true
            };
        }
        return null;
    }

    private releasesUrl: string = "";
    private locationService: IVssLocationService;
    private contributionService: IVssContributionService;
    private _isMounted: boolean;

    public constructor(props: IDeploymentProps, context: IVssComponentContext) {
        super(props, context);

        this.locationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");
        this.contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        try {
            this.releasesUrl = this.locationService.routeUrl("ms.vss-releaseManagement-web.hub-explorer-3-default-route", {
                project: this.props.projectName
            });
        } catch (e) {}

        this._isMounted = false;

        this.state = {
            deployments: null,
            workItemId: this.props.workItemId,
            isLoading: true,
            hasError: false
        };
    }

    public render(): JSX.Element {
        const { deployments, isLoading, hasError } = this.state;
        // Spinner will show when the component is fetch data.
        // In the meantime, component will retain the previous result.
        // This is to prevent UI flickering when user quickly switches between work items.
        return (
            <div className="deployment-control-container">
                {hasError && this.renderError()}
                {deployments && deployments.length > 0 ? this.renderDeploymentsDetails(deployments) : this.renderZeroData()}
                {isLoading && this.renderSpinner()}
            </div>
        );
    }

    public componentDidUpdate(prevProps: IDeploymentProps, prevState: IDeploymentState) {
        if (this.state.isLoading) {
            this.updateDeploymentsData();
        }
    }

    public componentDidMount(): void {
        this._isMounted = true;
        this.updateDeploymentsData();
        this.props.onRegisterWorkItemListener?.({
            onRefreshed: () => {
                this.setState({ isLoading: true, hasError: false });
            }
        });
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
    }

    public updateDeploymentsData = async (): Promise<void> => {
        const workItemId = this.state.workItemId;
        let deployments: ExternalDeployment[];
        let hasError = false;

        try {
            deployments = await this.fetchDeploymentsData();
        } catch (e) {
            deployments = [];
            hasError = true;
            publishDeploymentsIntegrationTrace(this.context.pageContext, "DataProvider failed", e);
        }

        if (this._isMounted && workItemId === this.props.workItemId) {
            const groupDeployments = hasError ? this.state.deployments : this.groupDeployments(deployments);
            this.setState({
                deployments: groupDeployments,
                isLoading: false,
                hasError: hasError
            });
        }
    };

    private localizedEnvironmentType(type: string): string {
        switch (type) {
            case "production":
                return Resources.Production;
            case "testing":
                return Resources.Testing;
            case "development":
                return Resources.Development;
            case "staging":
                return Resources.Staging;
            default:
                return Resources.Unmapped;
        }
    }

    private groupDeployments(deployments: ExternalDeployment[]): IDeploymentType[] {
        const groupDeployments: IDeploymentType[] = [];

        deployments
            .sort(
                (a, b) =>
                    environmentTypeOrderMap[b.environment.type] - environmentTypeOrderMap[a.environment.type] ||
                    b.statusDate.getTime() - a.statusDate.getTime()
            )
            .forEach((deployment: ExternalDeployment) => {
                let locDeploymentName = this.localizedEnvironmentType(deployment.environment.type);
                let deploymentType = groupDeployments.find(g => g.name === locDeploymentName);
                if (!deploymentType) {
                    deploymentType = {
                        name: locDeploymentName,
                        pipelines: []
                    };
                    groupDeployments.push(deploymentType);
                }

                let pipeline = deploymentType.pipelines.find(p => p.id == deployment.pipeline.id);
                if (!pipeline) {
                    pipeline = {
                        name: deployment.pipeline.displayName,
                        id: deployment.pipeline.id,
                        status: deployment.status,
                        date: deployment.statusDate,
                        url: deployment.pipeline.url,
                        stages: []
                    };

                    deploymentType.pipelines.push(pipeline);
                }

                const stage: IStage = {
                    name: deployment.environment.displayName,
                    runId: deployment.runId,
                    status: deployment.status,
                    sequenceNumber: deployment.sequenceNumber,
                    url: deployment.url,
                    date: deployment.statusDate
                };

                if (
                    // We only need to display the most recent run for each stage. We have the stages sorted by environment type(prod, staging, test, dev, unmapped) and then completion date,
                    // so we're adding just the first run (is the latest as is sorted DESC) for each stage and ignoring the others for each pipeline. If the
                    // stage has two runs with the same id, we look for the sequence number
                    !pipeline.stages.find(
                        s => s.name === stage.name && (s.runId > stage.runId || (s.runId === stage.runId && s.sequenceNumber > stage.sequenceNumber))
                    )
                ) {
                    pipeline.stages.push(stage);
                    if (pipeline.status === "successful") {
                        pipeline.status = stage.status;
                    }
                }
            });

        return groupDeployments;
    }

    private renderZeroData(): JSX.Element {
        publishDeploymentsTelemetry(this.context.pageContext, DeploymentsIntegrationCIActions.SHOW_ZERO_DATA);

        const releaseImageUrl = this.locationService.getAssetLocation("ms.vss-build-web/common-library/Nav-Launch.png");
        return (
            <div className="deployments-zero-data">
                <div aria-hidden={true}>
                    <Image className="zero-data-icon" src={releaseImageUrl} />
                </div>
                {this.getZeroDataMessage()}
            </div>
        );
    }

    private getZeroDataMessage(): JSX.Element {
        return (
            <span>
                {Resources.ZeroDataMessage1}
                {this.releasesUrl ? (
                    <Link href={this.releasesUrl} target="_blank">
                        {Resources.ZeroDataMessageLinkText}
                    </Link>
                ) : (
                    Resources.ZeroDataMessageLinkText
                )}
                {Resources.ZeroDataMessage2}{" "}
                <Link href={DeploymentIntegrationConstants.LearnMoreDeployments} target="_blank">
                    {Resources.ZeroDataLearnMore}
                </Link>
            </span>
        );
    }

    private renderError(): JSX.Element {
        return (
            <div className="deployments-zero-data">
                <span>{Resources.ErrorOccurred}</span>
            </div>
        );
    }

    private renderDeploymentsDetails(deployments: IDeploymentType[]): JSX.Element {
        publishDeploymentsTelemetry(
            this.context.pageContext,
            DeploymentsIntegrationCIActions.SHOW_DEPLOYMENTS,
            this.getPipelinesAndStagesCount(deployments)
        );

        return (
            <div>
                {deployments.map((deploymentType: IDeploymentType) => {
                    return (
                        <div key={deploymentType.name}>
                            <div className="body-m font-weight-semibold type-title">{deploymentType.name}</div>
                            {deploymentType.pipelines.map(pipeline => (
                                <PipelineCard pipeline={pipeline} key={pipeline.id} />
                            ))}
                        </div>
                    );
                })}
            </div>
        );
    }

    private renderSpinner(): JSX.Element {
        return (
            <div className="spinner-container">
                <Spinner className="loading-spinner" size={SpinnerSize.medium} label={Resources.Loading} />
            </div>
        );
    }

    private fetchDeploymentsData = async (): Promise<ExternalDeployment[]> => {
        return (
            (await this.contributionService.getDataAsync<ExternalDeployment[]>(
                "ms.vss-work-web.work-item-external-deployments-data-provider",
                {
                    workItemId: this.state.workItemId
                },
                /* ignoreCache */ true
            )) ?? []
        );
    };

    private getPipelinesAndStagesCount(deployments: IDeploymentType[]): { numPipelines: number; numStages: number } {
        let numPipelines = 0;
        let numStages = 0;

        deployments.forEach(d => {
            d.pipelines.forEach(p => {
                numPipelines++;

                numStages = numStages + p.stages.length;
            });
        });

        return {
            numPipelines,
            numStages
        };
    }
}
VssComponent.register("DeploymentControl", Deployment);
